import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_EMAIL = "contact@cityaistudio.com";

const initial = { name: "", email: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      setForm(initial);
      toast.success("Thank you — we'll be in touch within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please email us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-soft"
    >
      <div className="mx-auto max-w-5xl px-5 lg:px-10">
        <div className="text-center">
          <span className="chip">
            <span className="chip-dot" />
            <span className="font-mono text-[12px] text-ink-200">Contact</span>
          </span>
          <h2
            className="mt-8 headline mx-auto max-w-3xl"
            style={{ fontSize: "clamp(1.85rem, 4.2vw, 3.4rem)" }}
          >
            Book your free <em>AI consultation.</em>
          </h2>
          <p className="mt-7 max-w-xl mx-auto text-ink-200/75 text-[16.5px] lg:text-[17px] leading-[1.75] font-light">
            Tell us a little about your business. We&apos;ll reply within one
            business day with a clear next step — no sales decks.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          {/* Info column */}
          <aside className="lg:col-span-4 space-y-4">
            <InfoCard
              icon={<Mail size={16} strokeWidth={1.6} />}
              label="Email"
              value={BRAND_EMAIL}
              href={`mailto:${BRAND_EMAIL}`}
              testid="contact-email-link"
            />
            <InfoCard
              icon={<MapPin size={16} strokeWidth={1.6} />}
              label="Location"
              value="Zurich, Switzerland"
            />
            <InfoCard
              icon={<Clock size={16} strokeWidth={1.6} />}
              label="Response time"
              value="Within 1 business day"
            />
          </aside>

          {/* Form column */}
          <div className="lg:col-span-8">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="card-surface p-7 lg:p-10"
            >
              {submitted ? (
                <div data-testid="contact-success" className="text-center py-8">
                  <span className="chip">
                    <span className="chip-dot" />
                    <span className="font-mono text-[12px] text-ink-200">
                      Received
                    </span>
                  </span>
                  <h3
                    className="mt-6 tracking-tight text-ink-50"
                    style={{
                      fontSize: "clamp(1.8rem, 3.2vw, 2.4rem)",
                      fontWeight: 400,
                      letterSpacing: "-0.025em",
                    }}
                  >
                    Thank you.
                  </h3>
                  <p className="mt-4 text-ink-200/80 max-w-md mx-auto font-light">
                    We&apos;ve logged your request and will reach out within
                    one business day to schedule your consultation.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-send-another"
                    className="btn-pill-light mt-8 inline-flex"
                  >
                    Send another
                    <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-5">
                    <Field label="Name" required>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={onChange("name")}
                        data-testid="contact-input-name"
                        className="field"
                        placeholder="Your full name"
                      />
                    </Field>
                    <Field label="Email" required>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={onChange("email")}
                        data-testid="contact-input-email"
                        className="field"
                        placeholder="you@company.com"
                      />
                    </Field>
                    <Field label="Message" required>
                      <textarea
                        rows={5}
                        required
                        value={form.message}
                        onChange={onChange("message")}
                        data-testid="contact-input-message"
                        className="field resize-none"
                        placeholder="Tell us briefly about your business and what you'd like to achieve."
                      />
                    </Field>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-xs text-ink-200/60 max-w-sm">
                      By submitting, you agree to be contacted by City AI
                      Studio. We never share your data.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-button"
                      className="btn-pill-dark disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? "Submitting…" : "Book Free Consultation"}
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .field {
          width: 100%;
          background: #ffffff;
          border: 1px solid rgba(11, 14, 20, 0.08);
          border-radius: 14px;
          padding: 13px 16px;
          font-size: 14.5px;
          color: #0B0E14;
          font-family: inherit;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .field::placeholder { color: rgba(11,14,20,0.35); }
        .field:focus {
          outline: none;
          border-color: #3B82F6;
          box-shadow: 0 0 0 4px rgba(59,130,246,0.12);
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[12px] font-mono text-ink-200/70 mb-2 uppercase tracking-[0.14em]">
      {label}
      {required && <span className="text-brand-light"> *</span>}
    </span>
    {children}
  </label>
);

const InfoCard = ({ icon, label, value, href, testid }) => {
  const inner = (
    <>
      <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#EFF4FF] to-[#DCE6FE] border border-brand-glow/30 flex items-center justify-center text-brand">
        {icon}
      </div>
      <div>
        <div className="text-[11px] font-mono text-ink-200/70 uppercase tracking-[0.14em]">
          {label}
        </div>
        <div className="mt-1 text-[15px] font-medium text-ink-50">{value}</div>
      </div>
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        data-testid={testid}
        className="card-surface p-5 flex items-center gap-4 group hover:!translate-y-0"
      >
        {inner}
      </a>
    );
  }
  return (
    <div className="card-surface p-5 flex items-center gap-4 hover:!translate-y-0">
      {inner}
    </div>
  );
};

export default Contact;
